import api from './api';
import { determineCampaignStatus } from '../utils/calculations';

/**
 * Service for handling current (live) data operations
 * Maps to actual backend API endpoints
 */
class CurrentDataService {
  /**
   * Get enhanced dashboard metrics (main financial data)
   * @returns {Promise} Enhanced metrics data
   */
  static async getEnhancedMetrics() {
    try {
      // Request full year data instead of default 30 days
      const response = await api.get('/dashboard/enhanced-metrics/', {
        params: { 
          date_range: '365'  // Get full year data instead of default 30 days
        }
      });
      console.log('✅ [CurrentDataService] Enhanced Metrics API Response:', response.data);
      return response;
    } catch (error) {
      console.error('Error fetching enhanced metrics:', error);
      throw error;
    }
  }

  /**
   * Get brand summaries for current data
   * @param {Object} filters - Filter parameters
   * @returns {Promise} Brand summaries data
   */
  static async getBrandSummaries(filters = {}) {
    try {
      const response = await api.get('/reports/brand-summaries/', { 
        params: filters 
      });
      return response;
    } catch (error) {
      console.error('Error fetching current brand summaries:', error);
      throw error;
    }
  }

  /**
   * Get savings detail for performance table
   * @returns {Promise} Savings detail data
   */
  static async getSavingsDetail() {
    try {
      const response = await api.get('/reports/savings-detail/');
      return response;
    } catch (error) {
      console.error('Error fetching current savings detail:', error);
      throw error;
    }
  }

  /**
   * Get OOH campaigns data
   * @param {number} year - Year filter
   * @returns {Promise} OOH campaigns data
   */
  static async getOOHCampaigns(year = new Date().getFullYear()) {
    try {
      const response = await api.get('/reports/ooh-campaigns/', {
        params: { year }
      });
      return response;
    } catch (error) {
      console.error('Error fetching OOH campaigns:', error);
      throw error;
    }
  }

  /**
   * Get budget data for BudgetTab
   * @param {number} year - Year filter
   * @returns {Promise} Budget data with campaigns and monthly breakdown
   */
  static async getBudgetData(year = new Date().getFullYear()) {
    try {
      console.log('🚀 Fetching budget data from new endpoint...');
      const response = await api.get('/dashboard/budget-data/', {
        params: { year }
      });
      console.log('✅ Budget data fetched successfully:', response.data?.length || 0, 'brands');
      return response;
    } catch (error) {
      console.error('Error fetching budget data:', error);
      throw error;
    }
  }

  /**
   * Get all dashboard data in parallel for better performance
   * @returns {Promise} Combined dashboard data
   */
  static async getAllDashboardData() {
    try {
      console.log('🚀 Fetching core dashboard data (optimized)...');

      // Only fetch core endpoints needed for Overview
      const [enhancedMetricsResult, budgetDataResult] = await Promise.allSettled([
        this.getEnhancedMetrics(),
        this.getBudgetData()
      ]);

      const enhancedMetrics = enhancedMetricsResult.status === 'fulfilled'
        ? enhancedMetricsResult.value.data
        : null;

      const budgetData = budgetDataResult.status === 'fulfilled'
        ? budgetDataResult.value.data
        : [];

      if (enhancedMetricsResult.status === 'rejected') {
        console.warn('⚠️ Enhanced metrics failed:', enhancedMetricsResult.reason);
      }
      if (budgetDataResult.status === 'rejected') {
        console.warn('⚠️ Budget data failed:', budgetDataResult.reason);
      }

      console.log('✅ Core dashboard data fetched:', {
        enhancedMetrics: enhancedMetrics ? 'Available' : 'Failed',
        budgetData: Array.isArray(budgetData) ? `${budgetData.length} items` : 'Failed'
      });

      // Return only the data we fetched; other sections will lazy-load when their tabs open
      return {
        enhancedMetrics,
        brandSummaries: [],
        savingsDetail: [],
        oohCampaigns: [],
        budgetData
      };
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  }

  /**
   * Get campaign status using BudgetTab logic for comparison
   */
  static getBudgetTabStatus(campaign) {
    if (!campaign || !campaign.start_date || !campaign.end_date) {
      return 'planning';
    }
    
    const today = new Date();
    const actualCurrentMonth = today.getMonth() + 1; // 1-based
    const currentYear = today.getFullYear();
    
    // Business Rule: n-2 logic (same as BudgetTab)
    const calculationCurrentMonth = Math.max(1, actualCurrentMonth - 2);
    
    let calculationYear = currentYear;
    if (calculationCurrentMonth <= 0) {
      calculationYear = currentYear - 1;
      const adjustedMonth = calculationCurrentMonth + 12;
      var currentMonthDate = new Date(calculationYear, adjustedMonth - 1, 15);
    } else {
      var currentMonthDate = new Date(calculationYear, calculationCurrentMonth - 1, 15);
    }
    
    const campaignStart = new Date(campaign.start_date);
    const campaignEnd = new Date(campaign.end_date);
    
    if (currentMonthDate > campaignEnd) {
      return 'done'; // lowercase like BudgetTab
    } else if (currentMonthDate >= campaignStart && currentMonthDate <= campaignEnd) {
      return 'on-going';
    } else {
      return 'planning';
    }
  }

  /**
   * Calculate saving using Brand Summaries API data with n-2 logic
   * @param {Object} campaign - Campaign object from Brand Summaries API
   * @returns {number} Saving amount (0 if not DONE)
   */
  static calculateCampaignSaving(campaign) {
    // Use n-2 logic to determine campaign status
    const status = this.getBudgetTabStatus(campaign);
    
    // Debug: Log ALL campaign data to understand the structure
    console.log(`🔍 Campaign "${campaign.campaign_name}" RAW DATA:`, {
      ...campaign,
      status
    });
    
    // Only calculate saving if status is "done" (n-2 logic determines campaign is finished)
    if (status === 'done') {
      // FIXED: Calculate from media_types array instead of campaign totals
      let planningCost = 0;
      let actualCost = 0;
      
      // Sum from media_types if available
      if (campaign.media_types && Array.isArray(campaign.media_types)) {
        campaign.media_types.forEach(mediaType => {
          planningCost += parseFloat(mediaType.planning_cost || 0);
          actualCost += parseFloat(mediaType.actual_cost || 0);
        });
        console.log(`📊 Campaign "${campaign.campaign_name}" - Calculated from ${campaign.media_types.length} media types`);
      } else {
        // Fallback to campaign totals if no media_types
        planningCost = parseFloat(
          campaign.total_planning_cost ||
          campaign.planning_cost || 
          campaign.planning_total ||
          0
        );
        
        actualCost = parseFloat(
          campaign.total_actual_cost ||
          campaign.actual_cost || 
          campaign.actual_total ||
          0
        );
        console.log(`📊 Campaign "${campaign.campaign_name}" - Using campaign totals (no media_types)`);
      }
      
      // Calculate saving as planning - actual (ignore API saving field, calculate fresh)
      const calculatedSaving = planningCost - actualCost;
      
      console.log(`💰 Campaign "${campaign.campaign_name}" saving calculation:`, {
        status,
        planningCost: planningCost.toLocaleString(),
        actualCost: actualCost.toLocaleString(),
        calculatedSaving: calculatedSaving.toLocaleString(),
        apiSaving: campaign.saving || 'N/A',
        startDate: campaign.start_date,
        endDate: campaign.end_date,
        availableFields: Object.keys(campaign)
      });
      
      return calculatedSaving;
    } else {
      console.log(`⏸️ Campaign "${campaign.campaign_name}" status "${status}" - no savings calculated`);
    }
    
    // Return 0 for campaigns not marked as "done" by n-2 logic
    return 0;
  }

  /**
   * Calculate Media Type Savings using EXACT Campaigns.js logic
   * @param {Array} brandSummaries - Brand summaries data
   * @returns {Array} Media type savings data
   */
  static calculateMediaTypeSavingsUsingCampaignsLogic(brandSummaries) {
    if (!Array.isArray(brandSummaries)) return [];

    const mediaTypeMap = {};

    brandSummaries.forEach(brand => {
      const campaigns = brand.campaigns || [];
      campaigns.forEach(campaign => {
        // Debug: Check campaign status using both methods
        const budgetTabStatus = this.getBudgetTabStatus(campaign);
        const utilsStatus = determineCampaignStatus(campaign);
        
        console.log(`🔍 MediaType - Campaign "${campaign.campaign_name}" status comparison:`, {
          budgetTabStatus,
          utilsStatus,
          startDate: campaign.start_date,
          endDate: campaign.end_date,
          planningCost: campaign.planning_cost || campaign.planning_total,
          actualCost: campaign.actual_cost || campaign.actual_total
        });
        
        // Use EXACT Campaigns.js logic to calculate saving
        const saving = this.calculateCampaignSaving(campaign);
        
        console.log(`💰 MediaType - Campaign "${campaign.campaign_name}" saving result:`, {
          saving,
          status: budgetTabStatus
        });
        
        // Only process if there's actual saving (campaign is DONE with saving > 0)
        if (saving !== 0) {
          const mediaTypes = campaign.media_types || [];
          
          if (mediaTypes.length > 0) {
            // Distribute campaign saving proportionally across media types
            const totalMediaTypeCost = mediaTypes.reduce((sum, mt) => 
              sum + (parseFloat(mt.planning_cost) || 0), 0
            );
            
            mediaTypes.forEach(mediaType => {
              const mediaTypeName = mediaType.media_type || 'Unknown';
              const mediaTypePlanning = parseFloat(mediaType.planning_cost) || 0;
              
              // Proportional saving based on media type planning cost
              const proportionalSaving = totalMediaTypeCost > 0 
                ? (mediaTypePlanning / totalMediaTypeCost) * saving 
                : saving / mediaTypes.length;
              
              if (!mediaTypeMap[mediaTypeName]) {
                mediaTypeMap[mediaTypeName] = { saving: 0, campaigns: 0 };
              }
              
              mediaTypeMap[mediaTypeName].saving += proportionalSaving;
              mediaTypeMap[mediaTypeName].campaigns += 1;
            });
          } else {
            // If no media types, assign to "Unknown"
            const mediaTypeName = 'Unknown';
            if (!mediaTypeMap[mediaTypeName]) {
              mediaTypeMap[mediaTypeName] = { saving: 0, campaigns: 0 };
            }
            mediaTypeMap[mediaTypeName].saving += saving;
            mediaTypeMap[mediaTypeName].campaigns += 1;
          }
        }
      });
    });

    // Convert to array format
    return Object.entries(mediaTypeMap).map(([mediaType, data]) => ({
      mediaType,
      saving: Math.round(data.saving),
      campaigns: data.campaigns
    })).sort((a, b) => b.saving - a.saving);
  }

  /**
   * Calculate Digital Vendor Savings using EXACT Campaigns.js logic
   * @param {Array} brandSummaries - Brand summaries data
   * @returns {Array} Digital vendor savings data
   */
  static calculateDigitalVendorSavingsUsingCampaignsLogic(brandSummaries) {
    if (!Array.isArray(brandSummaries)) return [];

    const vendorMap = {};

    brandSummaries.forEach(brand => {
      const campaigns = brand.campaigns || [];
      campaigns.forEach(campaign => {
        // Debug: Check campaign status using both methods
        const budgetTabStatus = this.getBudgetTabStatus(campaign);
        const utilsStatus = determineCampaignStatus(campaign);
        
        console.log(`🔍 DigitalVendor - Campaign "${campaign.campaign_name}" status comparison:`, {
          budgetTabStatus,
          utilsStatus,
          startDate: campaign.start_date,
          endDate: campaign.end_date,
          planningCost: campaign.planning_cost || campaign.planning_total,
          actualCost: campaign.actual_cost || campaign.actual_total
        });
        
        // Use EXACT Campaigns.js logic to calculate saving
        const saving = this.calculateCampaignSaving(campaign);
        
        console.log(`💰 DigitalVendor - Campaign "${campaign.campaign_name}" saving result:`, {
          saving,
          status: budgetTabStatus
        });
        
        // Only process DONE campaigns (using n-2 logic)
        const status = this.getBudgetTabStatus(campaign);
        if (status === 'done') {
          const mediaTypes = campaign.media_types || [];
          
          // Filter digital media types and calculate savings from media_types level
          const digitalMediaTypes = mediaTypes.filter(mt => {
            const mediaType = (mt.media_type || '').toLowerCase();
            return mediaType.includes('digital') || 
                   mediaType.includes('social') || 
                   mediaType.includes('online') ||
                   mediaType.includes('facebook') ||
                   mediaType.includes('google') ||
                   mediaType.includes('youtube') ||
                   mt.digital_vendor ||
                   mt.vendor;
          });
          
          console.log(`🔍 Campaign "${campaign.campaign_name}" digital media types:`, {
            totalMediaTypes: mediaTypes.length,
            digitalMediaTypes: digitalMediaTypes.length,
            digitalTypes: digitalMediaTypes.map(mt => ({
              type: mt.media_type,
              sub_media_type: mt.sub_media_type,
              digital_vendor: mt.digital_vendor,
              planning: mt.planning_cost,
              actual: mt.actual_cost,
              vendor: mt.digital_vendor || mt.vendor || mt.platform || 'API Missing',
              allFields: Object.keys(mt)
            }))
          });
          
          if (digitalMediaTypes.length > 0) {
            digitalMediaTypes.forEach(mediaType => {
              // Calculate saving for THIS specific media type (not proportional)
              const mediaTypePlanning = parseFloat(mediaType.planning_cost) || 0;
              const mediaTypeActual = parseFloat(mediaType.actual_cost) || 0;
              const mediaTypeSaving = mediaTypePlanning - mediaTypeActual;
              
              // Use digital vendor from API data
              let vendorName = 'Unknown Digital Vendor';
              
              if (mediaType.digital_vendor && mediaType.digital_vendor !== 'nan' && mediaType.digital_vendor !== 'null') {
                // Use real vendor from API
                vendorName = mediaType.digital_vendor;
                
                // Normalize vendor names for better display
                if (vendorName === 'Facebook') {
                  vendorName = 'Meta (Facebook)';
                } else if (vendorName === 'Programmatic') {
                  vendorName = 'Google Ads';
                } else if (vendorName === 'YouTube') {
                  vendorName = 'YouTube';
                } else if (vendorName === 'Tiktok') {
                  vendorName = 'TikTok';
                }
              } else {
                // Fallback mapping based on sub_media_type if API doesn't have vendor
                const subMediaTypeName = (mediaType.sub_media_type || '').toLowerCase();
                
                if (subMediaTypeName.includes('display')) {
                  vendorName = 'Google Ads';
                } else if (subMediaTypeName.includes('social')) {
                  vendorName = 'Meta (Facebook)';
                } else if (subMediaTypeName.includes('video')) {
                  vendorName = 'YouTube';
                } else if (mediaType.media_type && mediaType.media_type.toLowerCase() === 'social') {
                  vendorName = 'Meta (Facebook)';
                } else {
                  vendorName = 'Digital Platform';
                }
              }
              
              console.log(`💻 Media Type "${mediaType.media_type}" (${mediaType.sub_media_type}) → API: "${mediaType.digital_vendor}" → Display: "${vendorName}"`);
              
              // Final normalization
              if (vendorName.toLowerCase().includes('facebook') || vendorName.toLowerCase().includes('meta')) {
                vendorName = 'Meta (Facebook)';
              } else if (vendorName.toLowerCase().includes('google') || vendorName.toLowerCase().includes('youtube')) {
                vendorName = 'Google (YouTube)';
              } else if (vendorName === 'Unknown Digital Vendor') {
                vendorName = 'Digital Platform';
              }
              
              console.log(`💰 Digital Media Type "${mediaType.media_type}" (${vendorName}):`, {
                planning: mediaTypePlanning.toLocaleString(),
                actual: mediaTypeActual.toLocaleString(),
                saving: mediaTypeSaving.toLocaleString()
              });
              
              if (!vendorMap[vendorName]) {
                vendorMap[vendorName] = { saving: 0, campaigns: 0 };
              }
              
              vendorMap[vendorName].saving += mediaTypeSaving;
              vendorMap[vendorName].campaigns += 1;
            });
          }
        }
      });
    });

    // Convert to array format
    return Object.entries(vendorMap).map(([vendor, data]) => ({
      vendor,
      saving: Math.round(data.saving),
      campaigns: data.campaigns
    })).sort((a, b) => b.saving - a.saving);
  }

  /**
   * Process data for Overview charts using real API data structure
   * @param {Object} dashboardData - Raw dashboard data
   * @returns {Object} Processed data for charts
   */
  static processOverviewData(dashboardData) {
    const { enhancedMetrics, brandSummaries, savingsDetail } = dashboardData;

    console.log('🔍 Processing overview data with real API structure:', {
      enhancedMetrics: enhancedMetrics ? 'Available' : 'Missing',
      brandSummaries: Array.isArray(brandSummaries) ? `Array with ${brandSummaries.length} items` : typeof brandSummaries,
      savingsDetail: Array.isArray(savingsDetail) ? `Array with ${savingsDetail.length} items` : typeof savingsDetail
    });
    
    // Debug brandSummaries structure for digital vendor analysis
    if (Array.isArray(brandSummaries) && brandSummaries.length > 0) {
      const sampleBrand = brandSummaries[0];
      const sampleCampaign = sampleBrand.campaigns?.[0];
      const sampleMediaType = sampleCampaign?.media_types?.[0];
      console.log('🔍 BrandSummaries structure sample:', {
        brand: sampleBrand.brand_name,
        campaignsCount: sampleBrand.campaigns?.length || 0,
        sampleCampaign: sampleCampaign ? {
          name: sampleCampaign.campaign_name,
          status: sampleCampaign.campaign_status,
          startDate: sampleCampaign.start_date,
          endDate: sampleCampaign.end_date,
          mediaTypesCount: sampleCampaign.media_types?.length || 0
        } : 'No campaigns',
        sampleMediaType: sampleMediaType ? {
          type: sampleMediaType.media_type,
          digitalVendor: sampleMediaType.digital_vendor,
          vendor: sampleMediaType.vendor,
          platform: sampleMediaType.platform,
          planningCost: sampleMediaType.planning_cost,
          actualCost: sampleMediaType.actual_cost
        } : 'No media types'
      });
    }

    // 1. Summary Cards Data - Prefer DB totals from enhanced-metrics (backend-calculated)
    let summaryCards = {
      givenCost: enhancedMetrics?.financial_metrics?.total_budget || 0,
      planningCost: enhancedMetrics?.financial_metrics?.total_planning || 0,
      actualCost: enhancedMetrics?.financial_metrics?.total_actual || 0,
      // CRITICAL: Use backend DB-calculated savings
      totalSavings: enhancedMetrics?.financial_metrics?.total_savings || 0,
      totalCampaigns: enhancedMetrics?.financial_metrics?.total_campaigns || 0
    };

    // Do NOT override backend savings with recalculation. Keep DB as source of truth.
    // If backend field is missing, we keep 0 rather than guessing.
    // However, if API provides per-campaign saving fields, derive total by summing rows to match requirement.
    try {
      let summedFromRows = 0;
      // Priority 1: savingsDetail items (campaign-level, assumed to be DB-derived)
      if (Array.isArray(savingsDetail) && savingsDetail.length > 0) {
        summedFromRows = savingsDetail.reduce((sum, item) => {
          const v = parseFloat(
            item.saving_total ?? item.total_saving ?? item.saving ?? 0
          ) || 0;
          return sum + v;
        }, 0);
      } else if (Array.isArray(brandSummaries) && brandSummaries.length > 0) {
        // Priority 2: brandSummaries → sum campaigns' DB saving fields
        summedFromRows = brandSummaries.reduce((sum, brand) => {
          const campaigns = brand.campaigns || [];
          const brandSum = campaigns.reduce((csum, c) => {
            const v = parseFloat(
              c.saving_total ?? c.total_saving ?? c.saving ?? 0
            ) || 0;
            return csum + v;
          }, 0);
          return sum + brandSum;
        }, 0);
      }
      if (summedFromRows && summedFromRows !== 0) {
        summaryCards.totalSavings = summedFromRows;
      }
    } catch (e) {
      console.warn('Unable to sum savings from rows:', e);
    }

    console.log('💰 Summary cards data (with done-only savings):', summaryCards);

    // 2. Cost Breakdown Chart Data (by Brand) - Use real API data
    let costBreakdownData = [];
    
    // Priority 1: Use brand_performance from enhanced-metrics BUT calculate savings from done campaigns only
    if (enhancedMetrics?.brand_performance && Array.isArray(enhancedMetrics.brand_performance)) {
      costBreakdownData = enhancedMetrics.brand_performance.map(brand => ({
        brand: brand.campaign_media_type__campaign__brand__brand_name || brand.brand_name || brand.brand,
        given: brand.brand_budget || brand.total_budget || 0,
        planning: brand.brand_planning || brand.total_planning || 0,
        actual: brand.brand_actual || brand.total_actual || 0,
        saving: 0 // Initialize to 0, will be calculated from done campaigns later
      }));
      console.log('📊 Using brand_performance data (savings will be calculated from done campaigns):', costBreakdownData);
    }
    
    // Priority 2: Use brandSummaries if available (sum from campaign saving fields; fallback to brand_savings)
    else if (Array.isArray(brandSummaries) && brandSummaries.length > 0) {
      costBreakdownData = brandSummaries.map(brand => {
        // Sum campaign-level savings to match row aggregation
        let brandSaving = 0;
        const campaigns = brand.campaigns || [];
        if (campaigns.length > 0) {
          brandSaving = campaigns.reduce((sum, c) => {
            const v = parseFloat(
              c.saving_total ?? c.total_saving ?? c.saving ?? 0
            ) || 0;
            return sum + v;
          }, 0);
        } else {
          // Fallback to brand-level savings if no campaigns array present
          brandSaving = parseFloat(brand.brand_savings || brand.total_savings || 0) || 0;
        }

        return {
          brand: brand.brand_name || brand.brand,
          given: brand.total_given || brand.total_budget || 0,
          planning: brand.total_planning_cost || brand.total_planning || 0,
          actual: brand.total_actual_cost || brand.total_actual || 0,
          saving: brandSaving
        };
      });
      console.log('📊 Using brandSummaries data with backend savings (if available):', costBreakdownData);
    }
    
    // Priority 3: Only use fallback if no real data available
    else if (summaryCards.givenCost > 0) {
      console.log('⚠️ No brand data available, creating proportional fallback');
      const totalBudget = summaryCards.givenCost;
      const totalPlanning = summaryCards.planningCost;
      const totalActual = summaryCards.actualCost;
      
      costBreakdownData = [
        { 
          brand: 'Heineken', 
          given: Math.round(totalBudget * 0.4), 
          planning: Math.round(totalPlanning * 0.4), 
          actual: Math.round(totalActual * 0.4), 
          saving: Math.round((totalPlanning - totalActual) * 0.4)
        },
        { 
          brand: 'Tiger', 
          given: Math.round(totalBudget * 0.35), 
          planning: Math.round(totalPlanning * 0.35), 
          actual: Math.round(totalActual * 0.35), 
          saving: Math.round((totalPlanning - totalActual) * 0.35)
        },
        { 
          brand: 'Larue', 
          given: Math.round(totalBudget * 0.25), 
          planning: Math.round(totalPlanning * 0.25), 
          actual: Math.round(totalActual * 0.25), 
          saving: Math.round((totalPlanning - totalActual) * 0.25)
        }
      ];
    }

    // 3. Media Type Savings - Use EXACT Campaigns.js logic
    let mediaTypeSavings = [];
    if (Array.isArray(brandSummaries) && brandSummaries.length > 0) {
      // Calculate from brand summaries using Brand Summaries API + n-2 logic
      mediaTypeSavings = this.calculateMediaTypeSavingsUsingCampaignsLogic(brandSummaries);
      console.log('🥧 Media Type Savings calculated using Brand Summaries API + n-2 logic:', {
        count: mediaTypeSavings.length,
        data: mediaTypeSavings,
        source: 'Campaigns.js Logic'
      });
      
      // If no savings from DONE campaigns, show appropriate message
      if (mediaTypeSavings.length === 0 || mediaTypeSavings.every(item => item.saving === 0)) {
        mediaTypeSavings = [
          { mediaType: 'No Done Campaigns', saving: 0, campaigns: 0 }
        ];
        console.log('🥧 No savings from DONE campaigns - all campaigns are PLANNING/ON-GOING');
      }
    } else {
      // Fallback if no brand summaries available
      mediaTypeSavings = [
        { mediaType: 'No Data Available', saving: 0, campaigns: 0 }
      ];
      console.log('🥧 No brand summaries data available');
    }

    // 4. Digital Vendor Savings - Use EXACT Campaigns.js logic
    let digitalVendorSavings = [];
    let digitalVendorDataSource = '';
    
    if (Array.isArray(brandSummaries) && brandSummaries.length > 0) {
      // Calculate from brand summaries using Brand Summaries API + n-2 logic  
      digitalVendorSavings = this.calculateDigitalVendorSavingsUsingCampaignsLogic(brandSummaries);
      digitalVendorDataSource = 'Brand Summaries API + n-2 Logic';
      console.log('🥧 Digital Vendor Savings calculated using Brand Summaries API + n-2 logic:', {
        count: digitalVendorSavings.length,
        data: digitalVendorSavings,
        source: digitalVendorDataSource
      });
      
      // If no savings from DONE digital campaigns, show appropriate message
      if (digitalVendorSavings.length === 0 || digitalVendorSavings.every(item => item.saving === 0)) {
        digitalVendorSavings = [
          { vendor: 'No Done Digital Campaigns', saving: 0, campaigns: 0 }
        ];
        digitalVendorDataSource = 'No Done Digital Campaigns';
        console.log('🥧 No digital vendor savings from DONE campaigns');
      }
    } else {
      // Fallback if no brand summaries available
      digitalVendorSavings = [
        { vendor: 'No Data Available', saving: 0, campaigns: 0 }
      ];
      digitalVendorDataSource = 'No Data Available';
      console.log('🥧 No brand summaries data available');
    }

    // 4.5. Update Cost Breakdown savings from done campaigns (if we have brand summaries)
    if (Array.isArray(brandSummaries) && brandSummaries.length > 0 && costBreakdownData.length > 0) {
      const brandSavingsMap = new Map();
      
      // Calculate savings by brand using Brand Summaries API + n-2 logic
      console.log('📊 Calculating Cost Breakdown savings by brand...');
      
      brandSummaries.forEach((brand, brandIndex) => {
        const campaigns = brand.campaigns || [];
        let brandSaving = 0;
        
        console.log(`🏢 Cost Breakdown - Brand ${brandIndex + 1}: "${brand.brand_name}"`);
        
        campaigns.forEach((campaign, campaignIndex) => {
          // Use Brand Summaries API + n-2 logic
          const saving = this.calculateCampaignSaving(campaign);
          brandSaving += saving;
          
          if (saving !== 0) {
            console.log(`  ✅ Campaign ${campaignIndex + 1}: "${campaign.campaign_name}" - Saving: ${saving.toLocaleString()}`);
          }
        });
        
        console.log(`🏢 Brand "${brand.brand_name}" total savings for cost breakdown: ${brandSaving.toLocaleString()}`);
        
        if (brandSaving !== 0) { // Include negative savings
          brandSavingsMap.set(brand.brand_name || brand.brand, brandSaving);
        }
      });
      
      // Update cost breakdown data with calculated savings
      costBreakdownData = costBreakdownData.map(brandData => ({
        ...brandData,
        saving: brandSavingsMap.get(brandData.brand) || 0
      }));
      
      console.log('📊 Updated cost breakdown with CAMPAIGNS.JS logic savings:', {
        totalBrands: costBreakdownData.length,
        savingsMap: Object.fromEntries(brandSavingsMap),
        totalSavings: Array.from(brandSavingsMap.values()).reduce((sum, val) => sum + val, 0)
      });
    }

    // 5. Performance Summary Table Data - Use savings detail or brand summaries campaigns (prefer backend savings fields)
    let performanceTableData = this.processPerformanceTableDataFromSavingsDetail(savingsDetail, brandSummaries);
    let performanceTableDataSource = savingsDetail?.length > 0 ? 'SavingsDetail' : 'BrandSummaries';
    
    // Fallback if no performance table data
    if (performanceTableData.length === 0 && summaryCards.totalCampaigns > 0) {
      console.log('⚠️ No campaign data, using calculated fallback');
      performanceTableDataSource = 'Fallback (No Campaign Data)';
      const avgActual = Math.round(summaryCards.actualCost / summaryCards.totalCampaigns);
      const avgPlanning = Math.round(summaryCards.planningCost / summaryCards.totalCampaigns);
      const avgSaving = Math.round(summaryCards.totalSavings / summaryCards.totalCampaigns);

      performanceTableData = [
        {
          brand: 'Heineken',
          campaignName: 'Summer Campaign 2025',
          mediaType: 'Digital',
          digitalVendor: 'Facebook',
          status: 'done',
          actualCost: avgActual,
          planningCost: avgPlanning,
          ytd: avgActual,
          ytg: avgPlanning - avgActual,
          saving: avgSaving,
          statusColor: '#4CAF50'
        },
        {
          brand: 'Tiger',
          campaignName: 'New Year Campaign',
          mediaType: 'OOH',
          digitalVendor: '-',
          status: 'done',
          actualCost: Math.round(avgActual * 0.8),
          planningCost: Math.round(avgPlanning * 0.8),
          ytd: Math.round(avgActual * 0.8),
          ytg: Math.round((avgPlanning - avgActual) * 0.8),
          saving: Math.round(avgSaving * 0.8),
          statusColor: '#4CAF50'
        },
        {
          brand: 'Larue',
          campaignName: 'Brand Awareness',
          mediaType: 'Print',
          digitalVendor: '-',
          status: 'on-going',
          actualCost: Math.round(avgActual * 0.6),
          planningCost: Math.round(avgPlanning * 0.6),
          ytd: Math.round(avgActual * 0.6),
          ytg: Math.round((avgPlanning - avgActual) * 0.6),
          saving: 0, // No saving for on-going campaigns
          statusColor: '#FF9800'
        }
      ];
    }

    return {
      summaryCards,
      costBreakdownData,
      mediaTypeSavings,
      digitalVendorSavings,
      performanceTableData,
      performanceRatios: enhancedMetrics?.performance_ratios || {},
      dataSource: {
        summaryCards: Array.isArray(brandSummaries) && brandSummaries.length > 0 ? 'Campaigns.js Logic' : 'Enhanced Metrics API Fallback',
        costBreakdown: enhancedMetrics?.brand_performance ? 'Enhanced Metrics API' : (brandSummaries?.length > 0 ? 'BrandSummaries' : 'Fallback'),
        mediaTypeSavings: Array.isArray(brandSummaries) && brandSummaries.length > 0 ? 'Campaigns.js Logic' : 'No Data',
        digitalVendorSavings: digitalVendorDataSource,
        performanceTable: performanceTableDataSource
      }
    };
  }



  /**
   * Calculate media type savings from brand summaries campaigns
   * @param {Array} brandSummaries - Brand summaries data
   * @returns {Array} Media type savings data
   */
  static calculateMediaTypeSavingsFromBrandSummaries(brandSummaries) {
    if (!Array.isArray(brandSummaries)) return [];

    const mediaTypeMap = {};

    brandSummaries.forEach(brand => {
      const campaigns = brand.campaigns || [];
      campaigns.forEach(campaign => {
        // Use EXACT same logic as Campaigns.js
        const status = determineCampaignStatus(campaign);
        
        // Debug: Log campaign status calculation
        console.log(`🔍 Campaign Status Debug: "${campaign.campaign_name}"`, {
          startDate: campaign.start_date,
          endDate: campaign.end_date,
          originalStatus: campaign.campaign_status,
          autoCalculatedStatus: status,
          finalStatus: status
        });
        
        // Calculate saving using EXACT Campaigns.js logic
        const campaignSaving = this.calculateCampaignSaving(campaign);
        
        console.log(`💰 Campaign "${campaign.campaign_name}" saving calculation:`, {
          campaignSaving,
          status
        });
        
        if (campaignSaving !== 0) { // Include negative savings too
            // Distribute campaign savings proportionally across media types
            const mediaTypes = campaign.media_types || [];
            const totalMediaPlanning = mediaTypes.reduce((sum, mt) => sum + (mt.planning_cost || 0), 0);
            
            mediaTypes.forEach(mediaType => {
              const type = mediaType.media_type || 'Unknown';
              const mediaPlanning = mediaType.planning_cost || 0;
              
              // Proportional savings based on media type's share of total planning
              const proportionalSaving = totalMediaPlanning > 0 
                ? (mediaPlanning / totalMediaPlanning) * campaignSaving 
                : campaignSaving / mediaTypes.length; // Equal distribution if no planning costs
              
              if (proportionalSaving !== 0) { // Include negative savings
                if (!mediaTypeMap[type]) {
                  mediaTypeMap[type] = { mediaType: type, saving: 0, campaigns: 0 };
                }
                mediaTypeMap[type].saving += proportionalSaving;
                mediaTypeMap[type].campaigns += 1;
              }
            });
        }
      });
    });

    // Return all media types with savings (positive or negative) from done campaigns
    const result = Object.values(mediaTypeMap)
      .filter(item => item.saving !== 0)
      .sort((a, b) => b.saving - a.saving);
    
    console.log('📺 Media Type Savings (CAMPAIGNS.JS LOGIC - DONE campaigns only):', {
      totalItems: result.length,
      items: result,
      source: 'Real Brand Summaries Data - CAMPAIGNS.JS Exact Logic'
    });
    return result;
  }

  /**
   * Calculate digital vendor savings from brand summaries campaigns
   * @param {Array} brandSummaries - Brand summaries data
   * @returns {Array} Digital vendor savings data
   */
  static calculateDigitalVendorSavingsFromBrandSummaries(brandSummaries) {
    if (!Array.isArray(brandSummaries)) return [];

    const vendorMap = {};

    console.log(`🔍 DigitalVendor - Processing ${brandSummaries.length} brands:`, brandSummaries.map(b => b.brand_name));
    
    brandSummaries.forEach(brand => {
      const campaigns = brand.campaigns || [];
      console.log(`🏢 Brand "${brand.brand_name}" has ${campaigns.length} campaigns:`, campaigns.map(c => c.campaign_name));
      
      campaigns.forEach(campaign => {
        // Use exact same logic as Campaigns.js
        const status = determineCampaignStatus(campaign);
        
        // Calculate saving using EXACT Campaigns.js logic
        const campaignSaving = this.calculateCampaignSaving(campaign);
        
        // Debug: Log campaign status for comparison with BudgetTab
        const statusFromUtils = determineCampaignStatus(campaign);
        const statusFromBudgetTabLogic = this.getBudgetTabStatus(campaign);
        
        console.log(`🔍 Campaign Status Comparison for "${campaign.campaign_name}":`, {
          utilsStatus: statusFromUtils,
          budgetTabStatus: statusFromBudgetTabLogic,
          campaignSaving: campaignSaving,
          startDate: campaign.start_date,
          endDate: campaign.end_date
        });
        
        // Only process campaigns that have savings (DONE status)
        if (campaignSaving !== 0) { // Include negative savings too
            const mediaTypes = campaign.media_types || [];
            const digitalMediaTypes = mediaTypes.filter(mt => mt.media_type?.toLowerCase() === 'digital');
            
            if (digitalMediaTypes.length > 0) {
              // Calculate total digital planning cost for proportional distribution
              const totalDigitalPlanning = digitalMediaTypes.reduce((sum, mt) => sum + (mt.planning_cost || 0), 0);
              const totalMediaPlanning = mediaTypes.reduce((sum, mt) => sum + (mt.planning_cost || 0), 0);
              
              // Campaign's digital portion of savings
              const digitalPortionOfSaving = totalMediaPlanning > 0 
                ? (totalDigitalPlanning / totalMediaPlanning) * campaignSaving
                : campaignSaving / mediaTypes.length * digitalMediaTypes.length;
              
              digitalMediaTypes.forEach(mediaType => {
                // Extract vendor from sub_media_type or other fields
                let vendor = mediaType.digital_vendor || 
                            mediaType.vendor || 
                            mediaType.platform || 
                            campaign.digital_vendor;
                
                // If no vendor specified, derive from sub_media_type
                if (!vendor && mediaType.sub_media_type) {
                  const subType = mediaType.sub_media_type.toLowerCase();
                  if (subType.includes('social')) {
                    vendor = 'Meta (Facebook)';
                  } else if (subType.includes('video')) {
                    vendor = 'Google (YouTube)';
                  } else if (subType.includes('search')) {
                    vendor = 'Google (Search)';
                  } else {
                    vendor = 'Digital Platform';
                  }
                } else if (!vendor) {
                  vendor = 'Digital Platform';
                }

                // Proportional savings for this digital vendor
                const mediaPlanning = mediaType.planning_cost || 0;
                const vendorSaving = totalDigitalPlanning > 0 
                  ? (mediaPlanning / totalDigitalPlanning) * digitalPortionOfSaving
                  : digitalPortionOfSaving / digitalMediaTypes.length;

                if (vendorSaving !== 0) { // Include negative savings
                  if (!vendorMap[vendor]) {
                    vendorMap[vendor] = { vendor, saving: 0, campaigns: 0 };
                  }
                  vendorMap[vendor].saving += vendorSaving;
                  vendorMap[vendor].campaigns += 1;
                }
              });
            }
        }
      });
    });

    // Return all vendors with savings (positive or negative) from done campaigns
    const result = Object.values(vendorMap)
      .filter(item => item.saving !== 0)
      .sort((a, b) => b.saving - a.saving);
    
    console.log('💻 Digital Vendor Savings (CAMPAIGNS.JS LOGIC - DONE campaigns only):', {
      totalBrands: brandSummaries?.length || 0,
      totalCampaigns: brandSummaries?.reduce((sum, brand) => sum + (brand.campaigns?.length || 0), 0) || 0,
      vendorMapKeys: Object.keys(vendorMap),
      resultCount: result.length,
      items: result,
      source: 'Real Brand Summaries Data - CAMPAIGNS.JS Exact Logic'
    });
    return result;
  }

  /**
   * Process performance table data from savings detail or brand summaries
   * @param {Array} savingsDetail - Savings detail data
   * @param {Array} brandSummaries - Brand summaries data as fallback
   * @returns {Array} Performance table data
   */
  static processPerformanceTableDataFromSavingsDetail(savingsDetail, brandSummaries) {
    // Priority 1: Use savings detail if available
    if (Array.isArray(savingsDetail) && savingsDetail.length > 0) {
      const result = savingsDetail.map(item => {
        // Use EXACT Campaigns.js logic
        const finalStatus = determineCampaignStatus(item);
        
        // Calculate saving using Brand Summaries API + n-2 logic
        const saving = this.calculateCampaignSaving(item);
        
        // FIXED: Calculate planning and actual from media_types (same as calculateCampaignSaving)
        let planning = 0;
        let actual = 0;
        
        if (item.media_types && Array.isArray(item.media_types)) {
          item.media_types.forEach(mediaType => {
            planning += parseFloat(mediaType.planning_cost || 0);
            actual += parseFloat(mediaType.actual_cost || 0);
          });
        } else {
          // Fallback to item totals
          planning = parseFloat(item.total_planning_cost || item.planning_cost || 0);
          actual = parseFloat(item.total_actual_cost || item.actual_cost || 0);
        }
        
        // Extract digital vendor from media types
        let digitalVendor = '-';
        if (item.media_types && Array.isArray(item.media_types)) {
          const digitalMediaType = item.media_types.find(mt => 
            mt.media_type?.toLowerCase() === 'digital'
          );
          if (digitalMediaType) {
            digitalVendor = digitalMediaType.digital_vendor || 
                          digitalMediaType.vendor || 
                          digitalMediaType.platform || 
                          'Digital Platform';
          }
        }
        
        return {
          brand: item.brand_name || 'Unknown',
          campaignName: item.campaign_name || 'Unknown Campaign',
          mediaType: item.media_types?.[0]?.media_type || 'Unknown',
          digitalVendor: digitalVendor,
          status: finalStatus,
          actualCost: actual,
          planningCost: planning,
          givenCost: parseFloat(item.total_given || 0), // Add Given Cost field
          ytd: item.ytd || actual,
          ytg: item.ytg || Math.max(0, planning - actual),
          saving: saving, // Only for DONE campaigns (Brand Summaries API + n-2 logic)
          statusColor: this.getStatusColor(finalStatus)
        };
      });
      console.log('📋 Using savings detail data (CAMPAIGNS.JS logic):', result.slice(0, 3));
      return result;
    }

    // Priority 2: Use brand summaries campaigns
    if (Array.isArray(brandSummaries) && brandSummaries.length > 0) {
      const campaigns = [];
      brandSummaries.forEach(brand => {
        const brandCampaigns = brand.campaigns || [];
        brandCampaigns.forEach(campaign => {
          // Use EXACT Campaigns.js logic
          const finalStatus = determineCampaignStatus(campaign);
          
          // Calculate saving using Brand Summaries API + n-2 logic
          const saving = this.calculateCampaignSaving(campaign);
          
          // FIXED: Calculate planning and actual from media_types (same as calculateCampaignSaving)
          let planning = 0;
          let actual = 0;
          
          if (campaign.media_types && Array.isArray(campaign.media_types)) {
            campaign.media_types.forEach(mediaType => {
              planning += parseFloat(mediaType.planning_cost || 0);
              actual += parseFloat(mediaType.actual_cost || 0);
            });
          } else {
            // Fallback to campaign totals
            planning = parseFloat(campaign.total_planning_cost || campaign.planning_cost || 0);
            actual = parseFloat(campaign.total_actual_cost || campaign.actual_cost || 0);
          }
          
          // Extract digital vendor from media types
          let digitalVendor = '-';
          if (campaign.media_types && Array.isArray(campaign.media_types)) {
            const digitalMediaType = campaign.media_types.find(mt => 
              mt.media_type?.toLowerCase() === 'digital'
            );
            if (digitalMediaType) {
              digitalVendor = digitalMediaType.digital_vendor || 
                            digitalMediaType.vendor || 
                            digitalMediaType.platform || 
                            'Digital Platform';
            }
          }
          
          campaigns.push({
            brand: brand.brand_name,
            campaignName: campaign.campaign_name,
            mediaType: campaign.media_types?.[0]?.media_type || 'Unknown',
            digitalVendor: digitalVendor,
            status: finalStatus,
            actualCost: actual,
            planningCost: planning,
            givenCost: parseFloat(campaign.total_given || 0), // Add Given Cost field
            ytd: campaign.ytd || actual,
            ytg: campaign.ytg || Math.max(0, planning - actual),
            saving: saving, // Only for DONE campaigns (Brand Summaries API + n-2 logic)
            statusColor: this.getStatusColor(finalStatus)
          });
        });
      });
      
      const result = campaigns;
      console.log('📋 Using brand summaries campaigns (CAMPAIGNS.JS logic):', result.slice(0, 3));
      return result;
    }

    return [];
  }

  /**
   * Get status color based on campaign status
   * @param {string} status - Campaign status
   * @returns {string} Color code
   */
  static getStatusColor(status) {
    switch (status?.toLowerCase()) {
      case 'done':
        return '#4CAF50';
      case 'active':
      case 'on-going':
      case 'planning':
        return '#FF9800';
      default:
        return '#666';
    }
  }
}

export default CurrentDataService; 